import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, Camera, Package, Search } from 'lucide-react';
import { SkeletonList } from '../components/ui/Skeleton';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import { Item, Project, Room, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getRoom, getRoomsByProject, createRoom } from '../services/roomService';
import { getItem, createItem, updateItem, deleteItem } from '../services/itemService';
import { getPhotosByItem, getPhotosByProject, deletePhoto, getTemporaryPhotos, associateTemporaryPhotos, clearTemporaryPhotos } from '../services/photoService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFormNavigation } from '../hooks/useFormNavigation';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import CameraComponent from '../components/CameraComponent';
import PhotoViewer from '../components/PhotoViewer';
import PhotoList from '../components/PhotoList';
import TemporaryPhotoComponent from '../components/TemporaryPhotoComponent';
import PhotoSelector from '../components/PhotoSelector';
import ROOM_TYPES from '../utils/roomTypes';
import { useFullScreen } from '../components/Layout';
import { useNotifications } from '../contexts/NotificationContext';
import AiAssistButton from '../components/AiAssistButton';
import { categorizeItem, polishText } from '../services/aiAssistService';

type ItemFormData = Omit<Item, 'id' | 'createdAt' | 'updatedAt'>;

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  last?: boolean;
}> = ({ label, required, error, children, last }) => (
  <div className={`flex items-center gap-3 px-4 py-3 min-h-[44px] ${last ? '' : 'inset-divider'}`}>
    <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  </div>
);

const ItemFormPage: React.FC = () => {
  const { projectId, roomId: routeRoomId, itemId } = useParams<{ projectId: string; roomId?: string; itemId?: string }>();
  const [searchParams] = useSearchParams();
  const roomId = routeRoomId || searchParams.get('roomId') || undefined;
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const isOnline = useOnlineStatus();
  const isEditMode = !!itemId;
  
  const [project, setProject] = useState<Project | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [availablePhotos, setAvailablePhotos] = useState<Photo[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showNewRoomModal, setShowNewRoomModal] = useState(false);
  const [newRoomType, setNewRoomType] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [showTempPhotos, setShowTempPhotos] = useState(false);
  const [tempPhotoCount, setTempPhotoCount] = useState(0);
  const [formChanged, setFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState<ItemFormData | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [formData, setFormData] = useState<ItemFormData>({
    projectId: projectId || '',
    roomId: roomId || '',
    name: '',
    category: '',
    description: '',
    estimatedValue: undefined,
    priority: 'medium',
    notes: '',
    pickupStatus: 'pending',
    pickupNotes: '',
    tags: []
  });
  
  const { setIsFullScreen } = useFullScreen();

  // Track mount state + the transient error timer so async photo handlers don't
  // setState after the component unmounts (#28/#42) and rapid failures don't
  // leave conflicting/stale error timers running (#43).
  const mountedRef = useRef(true);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  // Set a transient error that auto-clears, guarded against unmount and
  // overlapping timers.
  const showTransientError = (message: string) => {
    if (!mountedRef.current) return;
    setError(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setError(null);
      errorTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      
      try {
        setLoading(true);
        
        const parallelFetches: [
          Promise<Project | null>,
          Promise<Room[]>,
          Promise<Room | null>,
          Promise<Item | null>,
          Promise<Photo[]>,
          Promise<Photo[]>,
        ] = [
          getProject(projectId),
          getRoomsByProject(projectId),
          roomId ? getRoom(roomId) : Promise.resolve(null),
          isEditMode && itemId ? getItem(itemId) : Promise.resolve(null),
          isEditMode && itemId ? getPhotosByItem(projectId, itemId) : Promise.resolve([]),
          getPhotosByProject(projectId),
        ];

        const [fetchedProject, fetchedRooms, fetchedRoom, fetchedItem, fetchedPhotos, allProjectPhotos] = await Promise.all(parallelFetches);

        setProject(fetchedProject);
        setRooms(fetchedRooms);
        if (fetchedRoom) setRoom(fetchedRoom);
        
        if (isEditMode && fetchedItem) {
          const formDataFromItem = {
            projectId: fetchedItem.projectId,
            roomId: fetchedItem.roomId,
            name: fetchedItem.name,
            category: fetchedItem.category,
            description: fetchedItem.description,
            estimatedValue: fetchedItem.estimatedValue,
            priority: fetchedItem.priority,
            notes: fetchedItem.notes,
            pickupStatus: fetchedItem.pickupStatus,
            pickupNotes: fetchedItem.pickupNotes || '',
            tags: fetchedItem.tags || []
          };
          
          setFormData(formDataFromItem);
          setInitialFormData(formDataFromItem);
          
          if (fetchedItem.roomId) {
            const itemRoom = fetchedRooms.find(r => r.id === fetchedItem.roomId);
            if (itemRoom) setRoom(itemRoom);
          }
          
          setPhotos(fetchedPhotos);
          const availableProjectPhotos = allProjectPhotos.filter(photo => 
            !fetchedPhotos.find(itemPhoto => itemPhoto.id === photo.id) && !photo.itemId
          );
          setAvailablePhotos(availableProjectPhotos);
        } else {
          if (roomId) {
            const newFormData = { ...formData, roomId };
            setFormData(newFormData);
            setInitialFormData(newFormData);
          } else {
            setInitialFormData(formData);
          }
          
          const availableProjectPhotos = allProjectPhotos.filter((photo: Photo) => !photo.itemId);
          setAvailablePhotos(availableProjectPhotos);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, roomId, itemId, isEditMode]);
  
  // Add effect to check for temporary photos
  useEffect(() => {
    const tempPhotos = getTemporaryPhotos();
    setTempPhotoCount(tempPhotos.length);
  }, [showTempPhotos]);
  
  // Track form changes
  useEffect(() => {
    if (loading || !initialFormData) return;
    
    // Compare current form data with initial data
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setFormChanged(hasChanges);
  }, [formData, initialFormData, loading]);
  
  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);
  
  const handleSaveAndNavigate = async (): Promise<boolean> => {
    if (!projectId) return false;
    
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att spara objektet.');
      return false;
    }
    
    try {
      setSaving(true);
      
      // Create a clean copy of the form data with proper type handling
      const dataToSave: any = {
        ...formData
      };
      
      // Handle estimatedValue - remove if undefined to avoid Firestore errors
      if (formData.estimatedValue === undefined) {
        delete dataToSave.estimatedValue;
      }
      
      // If roomId is empty string, remove it completely from the object
      // This ensures it won't be sent to Firestore at all
      if (!dataToSave.roomId) {
        delete dataToSave.roomId;
      }
      
      if (isEditMode && itemId) {
        await updateItem(itemId, dataToSave);
        
        // Update initial form data to match current data
        setInitialFormData(formData);
        setFormChanged(false);
        
        return true;
      } else {
        // For new items, create the item and associate temporary photos if any
        const result = await createItem(dataToSave);
        
        // Associate temporary photos with the new item if there are any
        const tempPhotos = getTemporaryPhotos();
        if (tempPhotos.length > 0) {
          try {
            await associateTemporaryPhotos(projectId, result.id);
            await clearTemporaryPhotos();
          } catch (err) {
            console.error('Error associating temporary photos:', err);
          }
        }
        
        // Associate selected photos with the new item if any
        if (photos.length > 0) {
          try {
            const { updateDoc, doc } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const { serverTimestamp } = await import('firebase/firestore');
            
            // Update all selected photos to be associated with the new item
            const updatePromises = photos.map(photo => {
              const photoRef = doc(db, 'photos', photo.id);
              return updateDoc(photoRef, {
                itemId: result.id,
                updatedAt: serverTimestamp()
              });
            });
            
            await Promise.all(updatePromises);
          } catch (photoErr) {
            console.error('Error associating photos with new item:', photoErr);
            // Don't fail the item creation if photo association fails
          }
        }
        
        // Update initial form data to match current data (guard against a fast
        // unmount from the navigate below — #42).
        if (mountedRef.current) {
          setInitialFormData(formData);
          setFormChanged(false);
        }

        // Always navigate to the main items list, regardless of room assignment
        navigate(`/projects/${projectId}/rooms`);

        return true;
      }
    } catch (err) {
      console.error('Error saving item:', err);
      setError('Kunde inte spara objektet. Försök igen senare.');
      return false;
    } finally {
      setSaving(false);
    }
  };
  
  // Form navigation safety
  const {
    navigateSafely,
    showDialog,
    handleSave,
    handleDiscard,
    handleContinueEditing,
    isSaving
  } = useFormNavigation({
    onSave: handleSaveAndNavigate,
    hasUnsavedChanges: formChanged
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'estimatedValue') {
      // Allow both numeric and text values (like "50000-60000")
      // Store as string if it contains non-numeric characters, otherwise as number
      if (!value.trim()) {
        setFormData(prev => ({
          ...prev,
          [name]: undefined
        }));
      } else {
        // Check if it's a pure number
        const numericValue = parseInt(value, 10);
        if (!isNaN(numericValue) && value === numericValue.toString()) {
          // Pure number - store as number
          setFormData(prev => ({
            ...prev,
            [name]: numericValue
          }));
        } else {
          // Contains text or ranges - store as string
          setFormData(prev => ({
            ...prev,
            [name]: value
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      tags: [...(prev.tags || []), tagInput.trim()]
    }));
    setTagInput('');
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };
  
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const success = await handleSaveAndNavigate();

    if (success && isEditMode && itemId) {
      // Always redirect to main items list after editing, not room-specific view
      navigate(`/projects/${projectId}/rooms`);
    }
  };
  
  const handleDelete = () => {
    if (!itemId || !isEditMode) return;
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort objektet.');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemId || !projectId) return;
    setShowDeleteConfirm(false);
    try {
      setSaving(true);
      await deleteItem(itemId, projectId);
      navigate(`/projects/${projectId}/rooms`);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Kunde inte ta bort objektet. Försök igen senare.');
    } finally {
      setSaving(false);
    }
  };
  
  const refreshPhotos = async () => {
    if (!projectId || !itemId) return;
    try {
      setPhotos(await getPhotosByItem(projectId, itemId));
    } catch (err) {
      console.error('Error refreshing photos:', err);
    }
  };

  // Fires per saved photo while the camera stays open for the next shot.
  const handlePhotoTaken = (photoId: string) => {
    refreshPhotos();
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    refreshPhotos();
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!projectId || !photoId) return;
    
    try {
      setDeleteLoading(photoId);
      await deletePhoto(photoId, projectId);
      
      // Remove the deleted photo from the state
      setPhotos(photos.filter(photo => photo.id !== photoId));
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Kunde inte ta bort fotot. Försök igen senare.');
      
      // Hide the error message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setDeleteLoading(null);
    }
  };
  
  const handleCreateRoom = async () => {
    if (!projectId || !newRoomType) return;
    
    try {
      setCreatingRoom(true);
      
      const roomData = {
        projectId,
        name: newRoomType,
        notes: ''
      };
      
      const newRoomId = await createRoom(roomData);
      
      // Create a new room object with the returned ID
      const newRoom: Room = {
        id: newRoomId.id,
        projectId,
        name: newRoomType,
        notes: '',
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any
      };
      
      // Add the new room to the rooms list
      setRooms([...rooms, newRoom]);
      
      // Select the new room
      setFormData(prev => ({
        ...prev,
        roomId: newRoom.id
      }));
      
      // Close the modal
      setShowNewRoomModal(false);
      setNewRoomType('');
    } catch (err) {
      console.error('Error creating room:', err);
      setError('Kunde inte skapa rummet. Försök igen senare.');
    } finally {
      setCreatingRoom(false);
    }
  };
  
  // Add the handleImageError function
  const handleImageError = (photoId: string) => {
    console.error(`Failed to load image for photo ${photoId}`);
  };

  const handleAddPhoto = async (photo: Photo) => {
    if (photos.find(p => p.id === photo.id)) return;
    
    if (isEditMode && itemId) {
      // In edit mode, associate the photo with this item in the database
      try {
        const { updateDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const { serverTimestamp } = await import('firebase/firestore');
        
        const photoRef = doc(db, 'photos', photo.id);
        await updateDoc(photoRef, {
          itemId: itemId,
          updatedAt: serverTimestamp()
        });
        
        if (!mountedRef.current) return;
        // Update the photo with the new itemId
        const updatedPhoto = { ...photo, itemId: itemId };
        setPhotos(prev => [...prev, updatedPhoto]);
        setAvailablePhotos(prev => prev.filter(p => p.id !== photo.id));

        addNotification({ type: 'success', message: 'Foto kopplat till objektet', duration: 2000 });

      } catch (err) {
        console.error('Error associating photo with item:', err);
        showTransientError('Kunde inte koppla fotot till objektet. Försök igen senare.');
      }
    } else {
      // In create mode, just add to the local state
      setPhotos(prev => [...prev, photo]);
      setAvailablePhotos(prev => prev.filter(p => p.id !== photo.id));
    }
  };

  const handleRemovePhoto = async (photoId: string) => {
    const removedPhoto = photos.find(p => p.id === photoId);
    if (!removedPhoto) return;
    
    if (isEditMode && itemId) {
      // In edit mode, disassociate the photo from this item in the database
      try {
                 const { updateDoc, doc } = await import('firebase/firestore');
         const { db } = await import('../firebase');
         const { serverTimestamp } = await import('firebase/firestore');
         
         const photoRef = doc(db, 'photos', photoId);
         await updateDoc(photoRef, {
           itemId: null,
           updatedAt: serverTimestamp()
         });
        
        if (!mountedRef.current) return;
        // Remove the itemId from the photo
        const unassociatedPhoto = { ...removedPhoto, itemId: undefined };
        setPhotos(prev => prev.filter(p => p.id !== photoId));
        setAvailablePhotos(prev => [...prev, unassociatedPhoto]);

        addNotification({ type: 'success', message: 'Foto bortkopplat från objektet', duration: 2000 });

      } catch (err) {
        console.error('Error disassociating photo from item:', err);
        showTransientError('Kunde inte koppla bort fotot från objektet. Försök igen senare.');
      }
    } else {
      // In create mode, just remove from local state
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setAvailablePhotos(prev => [...prev, removedPhoto]);
    }
  };

  const handlePhotoSelectedFromLibrary = async (selectedPhoto: Photo) => {
    if (!projectId || !itemId) return;
    
    try {
      // Create a copy of the photo and associate it with this item
      // We need to update the photo's itemId in the database
      const { updateDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const { serverTimestamp } = await import('firebase/firestore');
      
      const photoRef = doc(db, 'photos', selectedPhoto.id);
      await updateDoc(photoRef, {
        itemId: itemId,
        updatedAt: serverTimestamp()
      });
      
      // Add the photo to the current photos list
      const updatedPhoto = { ...selectedPhoto, itemId: itemId };
      setPhotos(prev => [...prev, updatedPhoto]);
      
      addNotification({ type: 'success', message: 'Foto kopplat till objektet', duration: 2000 });
      
    } catch (err) {
      console.error('Error associating photo with item:', err);
      setError('Kunde inte koppla fotot till objektet. Försök igen senare.');
      
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  const backLink = roomId
    ? `/projects/${projectId}/rooms/${roomId}`
    : `/projects/${projectId}`;
  const backLabel = roomId ? 'Rum' : 'Projekt';
  const pageTitle = isEditMode ? 'Redigera föremål' : 'Nytt föremål';

  if (loading) {
    return (
      <PageShell title={pageTitle} backTo={backLink} backLabel={backLabel}>
        <div className="space-y-4 py-2">
          <SkeletonList count={5} />
        </div>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell title="Objekt" backTo="/" backLabel="Tillbaka">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          Projektet kunde inte hittas.
        </div>
      </PageShell>
    );
  }
  
  if (showCamera) {
    return (
      <CameraComponent
        projectId={projectId || ''}
        itemId={itemId}
        roomId={formData.roomId}
        onPhotoTaken={handlePhotoTaken}
        onClose={handleCameraClose}
      />
    );
  }

  return (
    <PageShell
      title={pageTitle}
      onBack={() => navigateSafely(backLink)}
      backLabel={backLabel}
      headerRight={
        <div className="flex items-center gap-4">
          {isEditMode && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-red-500 active:text-red-700 text-[17px] font-normal transition-colors"
            >
              Radera
            </button>
          )}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={saving}
            className={`text-[17px] font-semibold transition-colors ${
              saving
                ? 'text-[var(--text-tertiary)]'
                : 'text-accent-600 dark:text-accent-400 active:opacity-50'
            }`}
          >
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      }
    >
      <UnsavedChangesDialog
        isOpen={showDialog}
        onClose={handleContinueEditing}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />

      {selectedPhoto && (
        <PhotoViewer
          photos={[selectedPhoto]}
          initialPhotoIndex={0}
          onClose={() => setSelectedPhoto(null)}
          onDelete={async (photoId) => {
            await handleRemovePhoto(photoId);
            setSelectedPhoto(null);
          }}
        />
      )}

      {showPhotoSelector && (
        <PhotoSelector
          projectId={projectId || ''}
          excludePhotoIds={photos.map(p => p.id)}
          onPhotoSelected={handlePhotoSelectedFromLibrary}
          onClose={() => setShowPhotoSelector(false)}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort detta objekt? Denna åtgärd kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {showNewRoomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-surface)] rounded-xl shadow-xl p-4 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Skapa nytt rum</h2>

            <div className="mb-4">
              <label htmlFor="newRoomType" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                Rumstyp
              </label>
              <select
                id="newRoomType"
                value={newRoomType}
                onChange={(e) => setNewRoomType(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 transition-colors"
              >
                <option value="">Välj rumstyp</option>
                {ROOM_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNewRoomModal(false)}
                className="px-3 py-1.5 text-xs border border-[var(--border-default)] rounded-xl hover:bg-[var(--bg-surface-hover)]"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleCreateRoom}
                disabled={!newRoomType || creatingRoom}
                className={`px-3 py-1.5 text-xs rounded-xl text-white ${
                  !newRoomType || creatingRoom
                    ? 'bg-on-surface-tertiary cursor-not-allowed'
                    : 'bg-accent-600 hover:bg-accent-700'
                }`}
              >
                {creatingRoom ? 'Skapar...' : 'Skapa rum'}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6 pb-8">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-2 border-red-400 px-3 py-2.5 rounded-ios-card">
            <p className="text-[13px] text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Room Section */}
        <SectionGroup title="Rum">
          <FormRow label="Rum" required last>
            <div className="flex items-center gap-2">
              <select
                id="roomId"
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                required
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 appearance-none cursor-pointer"
              >
                <option value="" disabled>Välj rum</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewRoomModal(true)}
                className="flex items-center flex-shrink-0 text-accent-600 text-[15px] active:opacity-50"
              >
                <Plus size={16} className="mr-0.5" />
                Nytt
              </button>
            </div>
          </FormRow>
        </SectionGroup>

        {/* Item Details Section */}
        <div>
          <div className="flex items-center justify-between px-4 mb-1.5">
            <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Föremålsdetaljer</h3>
            {formData.name && !formData.category && (
              <AiAssistButton
                label="Auto-kategorisera"
                loadingLabel="Tänker..."
                onClick={async () => {
                  const result = await categorizeItem(formData.name, formData.description);
                  if (result && result.category) {
                    setFormData(prev => ({ ...prev, category: result.category }));
                  }
                }}
              />
            )}
          </div>
          <SectionGroup>
            <FormRow label="Namn" required>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                enterKeyHint="next"
                placeholder="Ange namn"
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0"
              />
            </FormRow>
            <FormRow label="Kategori">
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder={formData.name ? 'Tryck AI för förslag' : ''}
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 text-right"
              />
            </FormRow>
            <FormRow label="Värde">
              <input
                type="text"
                inputMode="numeric"
                name="estimatedValue"
                value={formData.estimatedValue || ''}
                onChange={handleInputChange}
                placeholder="t.ex. 5000"
                enterKeyHint="done"
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 text-right"
              />
            </FormRow>
            <FormRow label="Prioritet">
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 appearance-none cursor-pointer text-right"
              >
                <option value="low">Låg</option>
                <option value="medium">Medium</option>
                <option value="high">Hög</option>
              </select>
            </FormRow>
            <FormRow label="Status" last>
              <select
                name="pickupStatus"
                value={formData.pickupStatus}
                onChange={handleInputChange}
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 appearance-none cursor-pointer text-right"
              >
                <option value="pending">Ej hämtad</option>
                <option value="picked">Hämtad</option>
                <option value="skipped">Lämnad kvar</option>
              </select>
            </FormRow>
          </SectionGroup>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between px-4 mb-1.5">
            <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Beskrivning</h3>
            {formData.description && formData.description.trim().length > 5 && (
              <AiAssistButton
                label="Språkgranska"
                loadingLabel="Granskar..."
                onClick={async () => {
                  const result = await polishText(formData.description, 'föremålsbeskrivning');
                  if (result && result.text) {
                    setFormData(prev => ({ ...prev, description: result.text }));
                  }
                }}
              />
            )}
          </div>
          <SectionGroup>
            <div className="px-4 py-3">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                enterKeyHint="done"
                placeholder="Beskriv föremålet..."
                className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
              />
            </div>
          </SectionGroup>
        </div>

        {/* Notes */}
        <SectionGroup title="Anteckningar">
          <div className="px-4 py-3">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              enterKeyHint="done"
              placeholder="Anteckningar om föremålet..."
              className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
            />
          </div>
        </SectionGroup>

        {/* Pickup Notes */}
        {formData.pickupNotes && (
          <SectionGroup title="Hämtningsanteckningar">
            <div className="px-4 py-3">
              <textarea
                name="pickupNotes"
                value={formData.pickupNotes}
                onChange={handleInputChange}
                rows={3}
                enterKeyHint="done"
                placeholder="Anteckningar om hämtning..."
                className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
              />
            </div>
          </SectionGroup>
        )}

        {/* Tags */}
        <SectionGroup title="Taggar">
          <div className="px-4 py-3 space-y-2">
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center bg-[var(--bg-surface-alt)] text-[var(--text-primary)] text-[13px] px-2.5 py-1 rounded-full"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      aria-label={`Ta bort taggen ${tag}`}
                      className="ml-1.5 text-[var(--text-tertiary)] active:text-red-500"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Lägg till tagg..."
                className="flex-1 bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none py-0"
              />
              {tagInput.trim() && (
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="text-accent-600 text-[15px] font-medium active:opacity-50"
                >
                  Lägg till
                </button>
              )}
            </div>
          </div>
        </SectionGroup>

        {/* Photos */}
        <SectionGroup title="Foton">
          <div className="px-4 py-3 space-y-3">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                className="flex items-center text-accent-600 text-[15px] active:opacity-50"
              >
                <Camera size={16} className="mr-1" />
                Ta foto
              </button>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => setShowPhotoSelector(true)}
                  className="flex items-center text-accent-600 text-[15px] active:opacity-50"
                >
                  <Search size={16} className="mr-1" />
                  Välj från bibliotek
                </button>
              )}
              {!isEditMode && tempPhotoCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTempPhotos(true)}
                  className="flex items-center text-accent-600 text-[15px] active:opacity-50"
                >
                  <Package size={16} className="mr-1" />
                  Temporära ({tempPhotoCount})
                </button>
              )}
            </div>

            {showTempPhotos && !isEditMode && (
              <TemporaryPhotoComponent onClose={() => setShowTempPhotos(false)} />
            )}

            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img
                      src={photo.thumbnail || photo.url}
                      loading="lazy"
                      alt=""
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                      onError={() => handleImageError(photo.id)}
                    />
                    {deleteLoading === photo.id ? (
                      <div className="absolute top-1 right-1 bg-[var(--bg-surface)] rounded-full p-1 shadow">
                        <div className="animate-spin h-3.5 w-3.5 border-2 border-accent-500 border-t-transparent rounded-full"></div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(photo.id)}
                        aria-label="Ta bort foto"
                        className="absolute top-1 right-1 bg-black/50 rounded-full p-1 text-white active:bg-black/70"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {availablePhotos.length > 0 && (
              <div>
                <p className="text-[13px] text-[var(--text-tertiary)] mb-1.5">Tillgängliga projektfoton</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availablePhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-accent-500 transition-colors"
                      onClick={() => handleAddPhoto(photo)}
                    >
                      <img
                        src={photo.thumbnail || photo.url}
                        loading="lazy"
                        alt=""
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(photo.id)}
                      />
                      <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5 text-white">
                        <Plus size={12} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionGroup>
      </form>
    </PageShell>
  );
};

export default ItemFormPage; 